import CloseIcon from "@mui/icons-material/Close";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	InputAdornment,
	MenuItem,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { VForm, VMaskTextField, VTextField } from "../../../../shared/forms";
import { useToast } from "../../../../shared/hooks";
import { ICollectionPendingValue, SinisterService } from "../../../../shared/services/api/sinister/SinisterService";
import { globalRemoveMask } from "../../../../shared/utils";
import { formatDateFnsRemoveTimezone } from "../../../../shared/utils/format-date";
import { formatScaleValueBR } from "../../../../shared/utils/formatScaleValue";

interface Props {
	open: boolean;
	sinisterId: string;
	onClose: () => void;
}

// Mesmas opções de tipo de valor usadas em ModalAddValues (src/pages/sinister/components/Modals) —
// duplicada aqui de propósito, mesmo padrão já usado nesse componente e em VModalValueSinister, pra não
// forçar uma extração de constante compartilhada fora do escopo deste PR.
const SINISTER_VALUE_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
	{ value: "rent_values", label: "Aluguel" },
	{ value: "water_values", label: "Água" },
	{ value: "fire_insurance", label: "Apólice seguro incêndio" },
	{ value: "condominium_values", label: "Condomínio" },
	{ value: "damage", label: "Danos ao Imóvel" },
	{ value: "energy_values", label: "Energia Elétrica" },
	{ value: "iptu", label: "IPTU" },
	{ value: "gas_values", label: "Gás" },
	{ value: "cleaning", label: "Limpeza" },
	{ value: "fine_on_rent", label: "Multa sobre Aluguel" },
	{ value: "fine_on_condominium", label: "Multa sobre valor do condomínio" },
	{ value: "painting", label: "Pintura" },
	{ value: "garantti_tax", label: "Taxa mensal Garantti" },
	{ value: "other", label: "Outros" },
];

const getTypeLabel = (typeId: string) => SINISTER_VALUE_TYPE_OPTIONS.find((option) => option.value === typeId)?.label ?? typeId;

export const ModalLaunchFutureValue = ({ open, sinisterId, onClose }: Props) => {
	const toastHandler = useToast();
	const [pending, setPending] = useState<ICollectionPendingValue[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formKey, setFormKey] = useState(0);
	const [typeId, setTypeId] = useState("");
	const [reference, setReference] = useState("");
	const [dueDate, setDueDate] = useState("");
	const [valueInput, setValueInput] = useState("0,00");
	const [reason, setReason] = useState("");

	const fetchPending = () => {
		setIsLoading(true);
		SinisterService.getCollectionPendingValues(sinisterId).then((result) => {
			if (!(result instanceof Error)) setPending(result);
			setIsLoading(false);
		});
	};

	useEffect(() => {
		if (!open) return;
		setFormKey((prev) => prev + 1);
		setTypeId("");
		setReference("");
		setDueDate("");
		setValueInput("0,00");
		setReason("");
		fetchPending();
	}, [open, sinisterId]);

	const handleSubmit = async () => {
		const cleanedValue = globalRemoveMask(valueInput);
		if (!typeId || !reference || !dueDate || !cleanedValue || Number(cleanedValue) <= 0) {
			toastHandler.present({ type: "error", messageOrContent: "Preencha tipo, referência, vencimento e valor.", position: "top-right" });
			return;
		}
		if (!reason.trim()) {
			toastHandler.present({
				type: "error",
				messageOrContent: "Informe o motivo — é o único contexto disponível pra quem for confirmar depois, já que não há comprovante ainda.",
				position: "top-right",
			});
			return;
		}

		setIsSubmitting(true);
		const result = await SinisterService.launchFutureValue(sinisterId, {
			typeId,
			reference,
			dueDate,
			value: cleanedValue,
			reason,
		});
		setIsSubmitting(false);

		if (result instanceof Error) {
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
			return;
		}

		setPending((prev) => [...prev, result]);
		setFormKey((prev) => prev + 1);
		setTypeId("");
		setReference("");
		setDueDate("");
		setValueInput("0,00");
		setReason("");
		toastHandler.present({ type: "success", messageOrContent: "Valor futuro lançado com sucesso!", position: "top-right" });
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
			<LoadingComp isopen={isLoading || isSubmitting} />
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
					Lançar Valor Futuro
				</Typography>
				<IconButton onClick={onClose} size="small">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				<Stack spacing={2.5}>
					<Alert severity="info" sx={{ py: 0.5 }}>
						Pra um débito que ainda vai vencer e você já sabe que vai entrar no acordo. Fica pendente, sem aparecer no sinistro, até a equipe
						do sinistro confirmar (com comprovante) na tela dele.
					</Alert>

					<VForm
						key={formKey}
						onSubmit={() => {}}
						initialData={{}}
						onPointerEnterCapture={() => {}}
						onPointerLeaveCapture={() => {}}
						placeholder=""
					>
						<Stack spacing={2}>
							<TextField select fullWidth size="small" label="Tipo" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
								<MenuItem value="" disabled>
									Selecione…
								</MenuItem>
								{SINISTER_VALUE_TYPE_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</TextField>

							<VMaskTextField
								fullWidth
								size="small"
								label="Referência (MM/AAAA)"
								name="reference"
								mask="mesano"
								placeholder="09/2026"
								onChange={(e: any) => setReference(e?.target?.value ?? "")}
								onBlur={(e: any) => setReference(e?.target?.value ?? "")}
							/>

							<TextField
								type="date"
								fullWidth
								size="small"
								label="Vencimento"
								value={dueDate}
								onChange={(e) => setDueDate(e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>

							<VMaskTextField
								fullWidth
								size="small"
								label="Valor"
								name="value"
								mask="real"
								placeholder="0,00"
								onChange={(e: any) => setValueInput(e?.target?.value ?? "")}
								onBlur={(e: any) => setValueInput(e?.target?.value ?? "")}
								InputProps={{
									startAdornment: <InputAdornment position="start">R$</InputAdornment>,
								}}
							/>

							<VTextField
								fullWidth
								size="small"
								multiline
								minRows={2}
								label="Motivo"
								name="reason"
								placeholder="Ex.: aluguel de setembro, inquilino já avisou que não vai pagar."
								onChange={(e: any) => setReason(e?.target?.value ?? "")}
							/>
						</Stack>
					</VForm>

					<Button variant="contained" disabled={isSubmitting} onClick={handleSubmit} sx={{ alignSelf: "flex-start" }}>
						Lançar Valor
					</Button>

					<Divider />

					<Box>
						<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
							Pendentes de confirmação ({pending.length})
						</Typography>
						{pending.length === 0 ? (
							<Typography variant="body2" color="text.secondary">
								Nenhum valor futuro pendente pra esse sinistro.
							</Typography>
						) : (
							<TableContainer sx={{ maxHeight: 240 }}>
								<Table size="small" stickyHeader>
									<TableHead>
										<TableRow>
											<TableCell>Tipo</TableCell>
											<TableCell>Referência</TableCell>
											<TableCell align="center">Vencimento</TableCell>
											<TableCell align="right">Valor</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{pending.map((item) => (
											<TableRow key={item.id}>
												<TableCell>{getTypeLabel(item.typeId)}</TableCell>
												<TableCell>{item.reference}</TableCell>
												<TableCell align="center">{formatDateFnsRemoveTimezone(item.dueDate) || "-"}</TableCell>
												<TableCell align="right">{formatScaleValueBR(item.value)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Box>
				</Stack>
			</DialogContent>

			<DialogActions>
				<Button onClick={onClose}>Fechar</Button>
			</DialogActions>
		</Dialog>
	);
};
